import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import {
  EDGE_CLAMP,
  type EdgeMode,
  type Rect,
  type TensorDType,
  type TensorLayout,
} from '../types.js';

/** Shape and element type of a 4D activation tensor with batch size 1. */
export interface TensorSpec {
  width: number;
  height: number;
  channels: number;
  dtype: TensorDType;
  layout: TensorLayout;
}

export function tensorElementCount(spec: TensorSpec): number {
  return spec.width * spec.height * spec.channels;
}

export function tensorByteLength(spec: TensorSpec): number {
  const bytes = spec.dtype === 'float16' ? 2 : 4;
  // Round to 4 bytes: fp16 is stored as `pack2x16float` pairs of u32.
  return Math.ceil((tensorElementCount(spec) * bytes) / 4) * 4;
}

/** ONNX dims for the spec, in the order the layout implies. */
export function tensorDims(spec: TensorSpec): number[] {
  return spec.layout === 'nhwc'
    ? [1, spec.height, spec.width, spec.channels]
    : [1, spec.channels, spec.height, spec.width];
}

export interface PackOptions {
  /**
   * Source rectangle. May extend past the source bounds - the edge mode decides
   * what the out-of-bounds taps return, which is how tiled inference gets its
   * context padding for free.
   */
  rect?: Rect;
  edge?: EdgeMode;
  /** Per-channel normalisation: `(value - mean) / std`. */
  mean?: readonly number[];
  std?: readonly number[];
  /** Swap R and B for models exported with BGR inputs. */
  swapRB?: boolean;
}

/**
 * Texture -> ONNX tensor buffer, entirely on the GPU.
 *
 * The resulting buffer is handed to `ort.Tensor.fromGpuBuffer`, so no pixel data
 * ever crosses into JS memory between the decode and the model.
 */
export function packTensor(
  ctx: GpuContext,
  src: GpuImage,
  buffer: GPUBuffer,
  spec: TensorSpec,
  options: PackOptions = {},
): void {
  const rect = options.rect ?? { x: 0, y: 0, width: spec.width, height: spec.height };
  if (rect.width !== spec.width || rect.height !== spec.height) {
    throw new Error(
      `[gpu-core] packTensor reads 1:1; rect ${rect.width}x${rect.height} != tensor ${spec.width}x${spec.height}. Resample first.`,
    );
  }
  if (spec.dtype === 'float16') {
    if (spec.layout !== 'nchw') {
      throw new Error('[gpu-core] fp16 packing requires NCHW (pairs must be contiguous in x)');
    }
    if (spec.width % 2 !== 0) {
      throw new Error(`[gpu-core] fp16 packing requires an even tensor width, got ${spec.width}`);
    }
  }

  const mean = options.mean ?? [0, 0, 0, 0];
  const std = options.std ?? [1, 1, 1, 1];
  const inv = (i: number): number => 1 / (std[i] ?? std[0] ?? 1);

  const params = new Struct(80)
    .i(0, src.width, src.height)
    .i(8, rect.x, rect.y)
    .i(16, spec.width, spec.height)
    .u(24, options.edge ?? EDGE_CLAMP, 0)
    .f(32, mean[0] ?? 0, mean[1] ?? mean[0] ?? 0, mean[2] ?? mean[0] ?? 0, mean[3] ?? 0)
    .f(48, inv(0), inv(1), inv(2), inv(3))
    .u(64, spec.layout === 'nhwc' ? 1 : 0, spec.channels, options.swapRB ? 1 : 0, 0);

  const fp16 = spec.dtype === 'float16';
  ctx.dispatch(
    {
      module: 'tensor_pack.wgsl',
      entryPoint: fp16 ? 'pack_f16' : 'pack_f32',
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'buffer', access: 'read-write' },
        { kind: 'uniform' },
      ],
    },
    [src, buffer, ctx.uniform(params.bytes)],
    [workgroupCount(fp16 ? Math.ceil(spec.width / 2) : spec.width, 8), workgroupCount(spec.height, 8)],
    `packTensor ${spec.channels}x${spec.height}x${spec.width} ${spec.dtype}`,
  );
}

/** How to interpret tensor channels when writing them back to a texture. */
export const UNPACK_RGB = 0;
export const UNPACK_MASK = 1;
export const UNPACK_RGBA = 2;
export type UnpackMode = typeof UNPACK_RGB | typeof UNPACK_MASK | typeof UNPACK_RGBA;

export interface UnpackOptions {
  mode?: UnpackMode;
  /** Sub-rectangle of the tensor to copy. */
  srcOrigin?: readonly [number, number];
  copySize?: readonly [number, number];
  /** Where to write it in the destination texture. */
  dstOrigin?: readonly [number, number];
  /** De-normalisation: `value * scale + bias`. */
  scale?: readonly number[];
  bias?: readonly number[];
}

/**
 * ONNX tensor buffer -> texture.
 *
 * Writing a sub-rectangle to an arbitrary destination offset is what lets tiled
 * super-resolution skip seam blending: each tile is inferred with context
 * padding, and only its valid centre lands in the output.
 */
export function unpackTensor(
  ctx: GpuContext,
  buffer: GPUBuffer,
  dst: GpuImage,
  spec: TensorSpec,
  options: UnpackOptions = {},
): void {
  const [sx, sy] = options.srcOrigin ?? [0, 0];
  const [dx, dy] = options.dstOrigin ?? [0, 0];
  const [cw, ch] = options.copySize ?? [spec.width - sx, spec.height - sy];
  const scale = options.scale ?? [1, 1, 1, 1];
  const bias = options.bias ?? [0, 0, 0, 0];
  const at = (values: readonly number[], i: number): number => values[i] ?? values[0] ?? 0;

  const params = new Struct(80)
    .i(0, spec.width, spec.height)
    .i(8, sx, sy)
    .i(16, cw, ch)
    .i(24, dx, dy)
    .f(32, at(scale, 0), at(scale, 1), at(scale, 2), at(scale, 3))
    .f(48, at(bias, 0), at(bias, 1), at(bias, 2), at(bias, 3))
    .u(64, spec.dtype === 'float16' ? 1 : 0, spec.layout === 'nhwc' ? 1 : 0, spec.channels, options.mode ?? UNPACK_RGB);

  ctx.dispatch(
    {
      module: 'tensor_unpack.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'buffer', access: 'read-only' },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [buffer, dst, ctx.uniform(params.bytes)],
    [workgroupCount(cw, 8), workgroupCount(ch, 8)],
    `unpackTensor ${cw}x${ch}`,
  );
}
